import { ReactNode, useMemo } from 'react';
import { css } from '@emotion/react';
import { isFunction } from 'lodash-es';
import { theme } from '@frontend/theme';
import { useCompactTableContext } from '../../context/compact-table-context';
import { CompactTableRowContextType, CompactTableRowContext } from '../../context/compact-table-row-context';
import { AnimatedExpandedRow } from './atoms';
import { CompactTableData, CompactTableRowActionsProps } from './types';

export type CompactTableRowProps<T extends CompactTableData> = {
  rowElement: (row: T, contextValues: CompactTableRowContextType) => ReactNode;
  expandedRowElement: (row: T, contextValues: CompactTableRowContextType) => ReactNode;
  // memoize rowActions
  rowActions?: CompactTableRowActionsProps<T>;
  isRowDisabled?: (row: T) => boolean;
};

type CompactTableRowState<T extends CompactTableData> = {
  item: T;
  selectedRows: T[];
  expandedRows: Record<string, boolean>;
  className?: string;
  handleRowExpand: (item: any & CompactTableData, isExpanded: boolean) => void;
  handleRowSelect: (item: any & CompactTableData, isSelected: boolean) => void;
};

export const CompactTableRow = <T extends CompactTableData>({
  item,
  rowElement,
  expandedRowElement,
  expandedRows,
  selectedRows,
  className,
  isRowDisabled,
  rowActions,
  handleRowExpand,
  handleRowSelect,
}: CompactTableRowProps<T> & CompactTableRowState<T>) => {
  const { tableId, isSelectable = true, allExpandedByDefault, selectableRows } = useCompactTableContext();

  const isExpanded = expandedRows[item.id] ?? allExpandedByDefault;
  const isSelected = useMemo(() => {
    return !!selectedRows?.find((row) => row.id === item.id);
  }, [selectedRows, item.id]);
  const isDisabled = !!isRowDisabled?.(item);
  const isSelectionDisabled = !(selectableRows as T[]).some((row) => row.id === item.id);

  const values = useMemo(
    () => ({
      tableId,
      item,
      id: item.id,
      isExpanded,
      isSelected,
      isDisabled,
      isSelectionDisabled,
      handleRowSelect,
      handleRowExpand,
      rowActions,
      isSelectable,
    }),
    [
      tableId,
      item,
      isExpanded,
      isSelected,
      isDisabled,
      isSelectionDisabled,
      handleRowSelect,
      handleRowExpand,
      rowActions,
      isSelectable,
    ]
  );

  return (
    <CompactTableRowContext.Provider value={values}>
      <div css={[styles.compactTableRow]} className={className}>
        <div css={styles.rowElement(isExpanded)}>{isFunction(rowElement) && rowElement(item, values)}</div>
        <AnimatedExpandedRow isExpanded={isExpanded}>
          <div css={styles.expandedRowElement}>
            {isFunction(expandedRowElement) && expandedRowElement(item, values)}
          </div>
        </AnimatedExpandedRow>
      </div>
    </CompactTableRowContext.Provider>
  );
};

const styles = {
  compactTableRow: css`
    display: flex;
    flex-direction: column;
    border: 1px solid ${theme.colors.neutral20};
    margin-bottom: ${theme.spacing(1)};
    background-color: ${theme.colors.white};
    border-radius: ${theme.borderRadius.medium};
    transition: all 0.2s ease;
    overflow: hidden;
  `,
  rowElement: (isExpanded = false) => css`
    width: 100%;
    padding: ${theme.spacing(0.5, 1)};
    transition: border-bottom 0.3s ease;
    ${isExpanded ? `border-bottom: 1px solid ${theme.colors.neutral20};` : `border-bottom: 1px solid transparent;`}
  `,
  expandedRowElement: css`
    padding: ${theme.spacing(1)};
  `,
};
